<template>
  <div class="jps-mygallery">
    <UiSideMenu width="18rem" :is-clicked="isClicked">
      <div class="side-menu-item">
        <BaseLink to="/mynote">{{
          ls({ ja: 'マイノート', en: 'My Notes' })
        }}</BaseLink>
      </div>
      <UiSideMenuList class="side-menu-item">
        <template #head="">
          <h3>
            {{ ls({ ja: 'マイギャラリー', en: 'My Galleries' }) }}
          </h3>
          <button @click="sortWorkspace()">
            {{ ls({ ja: '並べ替え', en: 'Sort' }) }}
          </button>
          <UiSortModal ref="sortModal" :values="workspaces" :renderer="(workspace) => workspace.name" />
        </template>
        <template #list="">
          <li v-for="ws in workspaces" :key="ws.id">
            <button
              class="workspace-list-item"
              :class="{
                'jps-active': workspaceManagerStore.activeWorkspaceId === ws.id,
              }"
              @click.stop="() => {
                workspaceManagerStore.activeWorkspaceId = ws.id
                isClicked = !isClicked
              }"
            >
              {{ ws.name }}
              <BaseIcon v-if="ws.onlineId" name="mdi/web" />
            </button>
          </li>
        </template>
      </UiSideMenuList>
      <div class="side-menu-item">
        <BaseLink to="/mysearch">{{
          ls({ ja: 'マイサーチ', en: 'My Search' })
        }}</BaseLink>
      </div>
      <template #footer="">
        <div class="faq-block">
          <div class="faq-header">
            <BaseIcon name="mdi/help-circle-outline" />
            <span>マイギャラリーに関連するFAQ</span>
          </div>
          <div class="faq-links">
            <BaseLink class="faq-link" to="/userguide/05">マイギャラリーの基本的な使い方が知りたい</BaseLink>
            <BaseLink class="faq-link" to="/userguide/06">マイギャラリーへの作品・画像の追加方法は？</BaseLink>
            <BaseLink class="faq-link" to="/userguide/07">マイギャラリーを作りこむためのヒント（ジャパンサーチ以外の画像・動画の追加など）</BaseLink>
            <BaseLink class="faq-link" to="/userguide/08">作ったギャラリーを他の人と共有するには？</BaseLink>
          </div>
          <div class="faq-separator"></div>
          <BaseLink class="faq-link" to="/userguide">ジャパンサーチ利用ガイド</BaseLink>
        </div>
      </template>
    </UiSideMenu>
    <UiEditLayout class="mygallery-main" id="jps-curation-editor">
      <template #tools="">
        <UiEditLayoutButton type="NEW_GALLERY" @click="addWorkspace()" />
        <UiEditLayoutButton type="COPY" @click="copyWorkspace()" :disabled="!singleOpActive" />
        <UiEditLayoutButton type="DELETE" @click="deleteWorkspace()" :disabled="!singleOpActive" />
        <UiEditLayoutButton type="IMPORT" @click="importAll()" :disabled="!singleOpActive" />
        <UiEditLayoutButton type="EXPORT" @click="exportAll()" :disabled="!singleOpActive" />

        <BaseLink
          to="/mygallery/online"
          class="is-primary"
          @click.native="workspaceManagerStore.activeOnlineWorkspaceId = null"
        >
          <span class="el-label">
            {{
              lss(
                '招待されたギャラリーに入室する',
                'Join the invited Gallery',
              )
            }}
          </span>
        </BaseLink>
      </template>
      <MygalleryWorkspace ref="workspace" :workspace="activeWorkspace" />
    </UiEditLayout>
    <UiInputModal ref="inputModalAddWorkspace" :title="{ ja: '新しいギャラリーの名前', en: 'Title for new gallery' }" />
    <UiInputModal ref="inputModalCopyWorkspace" :title="{ ja: 'コピー先のタイトル', en: 'Title for copied note' }" />
    <UiConfirmModal ref="confirmModal" />
    <GalleryImportModal ref="galleryImportModal" />
  </div>
</template>

<script lang="ts" setup>
definePageMeta({
  disableFooter: true,
  disableDefaultMargin: true,
})

import type { UiSortModal, UiInputModal, UiConfirmModal, GalleryImportModal } from '#components'

const { getPageAndCache } = usePage()
const { meta } = await getPageAndCache()
useHead(meta)

const isClicked = ref(false)

const { ls, lss, lsst } = useI18n()
const { snackbarSuccess, snackbarError } = useSnackbar()
const { setLoading, loadingExecuteAndNotify } = useLoader()
const curationStore = useCurationStore()
const workspaceStore = useWorkspaceStore()
const workspaceManagerStore = useWorkspaceManagerStore()

const workspaces: Ref<WorkspaceEntry[]> = ref([])

onBeforeMount(async () => {
  await sync()
  workspaceStore.addListener(sync)
})

onBeforeUnmount(() => {
  workspaceStore.removeListener(sync)
})

const singleOpActive = computed(() => {
  return activeWorkspace.value != null
})

const activeWorkspace = computed(() => {
  if (workspaceManagerStore.activeWorkspaceId) {
    return workspaces.value
      .filter((ws) => ws.id === workspaceManagerStore.activeWorkspaceId)
      .pop()
  } else return null
})

const inputModalAddWorkspace = ref<InstanceType<typeof UiInputModal>>()
const addWorkspace = async () => {
  try {
    const text = await inputModalAddWorkspace.value.openAwait()
    if (!isEmpty(text)) {
      workspaceManagerStore.activeWorkspaceId = await workspaceStore.addWorkspace(text)
    }
  } catch (cancel) {}
}

const sortModal = ref<InstanceType<typeof UiSortModal>>()
const sortWorkspace = async () => {
  try {
    let sorted: any[] = await sortModal.value.openAwait()
    await workspaceStore.sort(sorted.map((we) => we.id))
  } catch (cancel) {}
}

const inputModalCopyWorkspace = ref<InstanceType<typeof UiInputModal>>()
const copyWorkspace = async  () => {
  if (!workspaceManagerStore.activeWorkspaceId) return
  const ws = await workspaceStore.get(
    workspaceManagerStore.activeWorkspaceId
  )
  const text = await inputModalCopyWorkspace.value.openAwait()
  if (!text) return
  let copiedWorkspaceId = await workspaceStore.addWorkspace(
    text,
    ws.description
  )
  let entries: CurationEntry[] = await curationStore.listGalleries(ws.id)
  for (let i = 0; i < entries.length; i++) {
    let entry = entries[i];
    forEachParts(entry.c.parts, (cp) => {
      cp.cpid = hashId()
    })
    entry.c.id = hashId()
    await curationStore.addGallery(entry.c, i, copiedWorkspaceId)
  }
  snackbarSuccess(
    {
      ja: ws.name + 'をコピーしました',
      en: ws.name + ' is coped',
    },
  )
}

const exportAll = async () => {
  if (!workspaceManagerStore.activeWorkspaceId) return
  try {
    setLoading(true)
    const curations = (
      await curationStore.listGalleries(
        workspaceManagerStore.activeWorkspaceId
      )
    ).map((ce) => ce.c)
    if (curations?.length) {
      let jsonl = ''
      for (let c of curations) {
        jsonl +=
          JSON.stringify((await curationStore.getCuration(c.id)).c) + '\n'
      }
      exportText(jsonl, activeWorkspace.value.name + '.jsonl')
    }
  } catch {
  } finally {
    setLoading(false)
  }
}

const galleryImportModal = ref<InstanceType<typeof GalleryImportModal>>()
const importAll = async () => {
  try {
    const result = await galleryImportModal.value.openAwait()
    function toGallery(c: Curation): Curation {
      delete c['version']
      delete c['seqNo']
      delete c['primaryTerm']
      delete c.id
      delete c.curationType
      delete c.workspace
      delete c.permission
      delete c.edit
      delete c.subPages
      return {
        ...c,
        id: null,
        curationType: 'GALLERY',
      }
    }
    let list = await curationStore.listGalleries(
      workspaceManagerStore.activeWorkspaceId
    )
    if (Array.isArray(result)) {
      let curations = result as Curation[]
      for (let c of curations) {
        try {
          await curationStore.addGallery(
            toGallery(c),
            list.length + 1,
            workspaceManagerStore.activeWorkspaceId
          )
          snackbarSuccess(
            { ja: ls(c.title) + 'のギャラリーを作成しました' },
          )
        } catch (error) {
          snackbarError(
            {
              ja: ls(c.title) + 'の作成に失敗しました。',
              en: ls(c.title) + ' creation failed',
            },
          )
        }
      }
      list = await curationStore.listGalleries(
        workspaceManagerStore.activeWorkspaceId
      )
    } else {
      try {
        let curation = result as Curation
        await curationStore.addGallery(
          toGallery(curation),
          list.length + 1,
          workspaceManagerStore.activeWorkspaceId
        )
        snackbarSuccess(
          {
            ja: ls(curation.title) + 'を作成しました',
            en: ls(curation.title) + ' has created',
          },
        )
      } catch (error) {
        console.info(error)
        snackbarError(
          {
            ja: 'ギャラリーの作成に失敗しました。もう一度お試しください。',
            en: 'Failed to create gallery. Try Again.',
          },
        )
      }
    }
  } catch (cancel) {
  } finally {
    setLoading(false)
  }
}

const confirmModal = ref<InstanceType<typeof UiConfirmModal>>()
const deleteWorkspace = async () => {
  try {
    if (!workspaceManagerStore.activeWorkspaceId) return
    const ws = await workspaceStore.get(
      workspaceManagerStore.activeWorkspaceId
    )
    const confirm = await confirmModal.value.openAwait({
      message: { ja: '{}を削除してよろしいですか？', en: 'Will you delete {}?' },
      messageVars: [ws.name]
    })
    if (!confirm) return
    await loadingExecuteAndNotify(
      lsst('{}を削除しました', '{} is deleted', ws.name),
      workspaceStore.deleteWorkspace(
        workspaceManagerStore.activeWorkspaceId
      )
    )
  } catch (cancel) {}
}

const sync = async () => {
  workspaces.value = await workspaceStore.getAll()
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-mygallery {
  height: $partial-scroll-height;
  display: flex;
  background-color: $jps-gray-50;
  .mygallery-main {
    flex-grow: 1;
  }
  .gallery-link {
    margin-left: auto;
  }
  .faq-block {
    padding: 20px 16px;
  }
  .faq-header {
    display: flex;
    align-items: center;
    color: $jps-gray-800;
    font-size: $size-7;
    font-weight: bold;
    margin-bottom: 8px;
  }
  .faq-links {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 10px;
  }
  .faq-link {
    color: $jps-key-blue;
    font-size: $size-7;
    line-height: 1.5;
  }
  .faq-separator {
    border-top: $basic-border;
    margin: 8px 0;
  }
  .mygallery-button-list {
    display: flex;
    gap: 0.75rem;
    flex-grow: 1;
    @include is-mobile {
      justify-content: flex-end;
    }
  }
}
</style>
